/**
 * markerGlyphs.ts — baked canvas IconDef glyphs for flood, ENE, power-outage,
 * and harbor-buoy map markers.
 *
 * Each glyph is rasterized once per distinct key and cached in a module-level Map.
 */

const GLYPH_SCALE = 3; // hi-dpi atlas scale (named constant, no magic numbers)

export interface IconDef {
  id: string;
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: false;
}

/** Anchor descriptor; values are in logical (pre-scale) pixels. */
interface AnchorPx {
  x: number;
  y: number;
}

const glyphCache = new Map<string, IconDef>();

/**
 * Bake a single square glyph canvas and return its IconDef.
 * Cached by `key` — each distinct key rasterizes exactly once.
 *
 * @param key   unique cache key
 * @param sizePx  logical render size in pixels (canvas will be sizePx*GLYPH_SCALE square)
 * @param draw  draw function called with a pre-scaled context and logical size
 * @param anchor  anchor in logical pixels (converted to atlas px internally)
 */
function bake(
  key: string,
  sizePx: number,
  draw: (ctx: CanvasRenderingContext2D, s: number) => void,
  anchor: AnchorPx,
): IconDef {
  const cached = glyphCache.get(key);
  if (cached) return cached;

  const atlasPx = sizePx * GLYPH_SCALE;
  const canvas = document.createElement('canvas');
  canvas.width = atlasPx;
  canvas.height = atlasPx;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(GLYPH_SCALE, GLYPH_SCALE);
  draw(ctx, sizePx);

  const def: IconDef = {
    id: key,
    url: canvas.toDataURL(),
    width: atlasPx,
    height: atlasPx,
    anchorX: Math.round(anchor.x * GLYPH_SCALE),
    anchorY: Math.round(anchor.y * GLYPH_SCALE),
    mask: false,
  };
  glyphCache.set(key, def);
  return def;
}

// ── FLOOD: water droplet, severity color, white wave inside ──────────────────
// Ported verbatim from vitals-marker-icons-preview.html → drawDroplet()

function drawDroplet(ctx: CanvasRenderingContext2D, s: number, color: [number, number, number]): void {
  const cx = s / 2;
  const topY = s * 0.08;
  const w = s * 0.34;       // half-width at the belly
  const bellyY = s * 0.62;
  const tipY = s * 0.96;

  ctx.beginPath();
  ctx.moveTo(cx, topY);
  // right side down to belly
  ctx.bezierCurveTo(cx + w * 0.55, topY + s * 0.14, cx + w, bellyY - s * 0.18, cx + w, bellyY);
  // right belly curving to the tip
  ctx.bezierCurveTo(cx + w, bellyY + s * 0.22, cx + w * 0.5, tipY, cx, tipY);
  // left tip up to belly
  ctx.bezierCurveTo(cx - w * 0.5, tipY, cx - w, bellyY + s * 0.22, cx - w, bellyY);
  // left belly back to top
  ctx.bezierCurveTo(cx - w, bellyY - s * 0.18, cx - w * 0.55, topY + s * 0.14, cx, topY);
  ctx.closePath();
  ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
  ctx.fill();
  ctx.lineWidth = s * 0.05;
  ctx.strokeStyle = 'rgba(6,10,18,0.55)';
  ctx.stroke();

  // white wave across the belly = standing water (not a rain drop)
  const wy = bellyY + s * 0.02;
  const amp = s * 0.05;
  ctx.beginPath();
  ctx.moveTo(cx - w * 0.62, wy);
  ctx.quadraticCurveTo(cx - w * 0.31, wy - amp, cx, wy);
  ctx.quadraticCurveTo(cx + w * 0.31, wy + amp, cx + w * 0.62, wy);
  ctx.lineWidth = s * 0.07;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.stroke();
}

// ── ENE: rounded sign badge, status color, white pictogram ───────────────────
// Ported verbatim from vitals-marker-icons-preview.html → eneBadge(), drawElevator(), drawEscalator()

function eneBadge(
  ctx: CanvasRenderingContext2D,
  s: number,
  color: [number, number, number],
  draw: (ctx: CanvasRenderingContext2D, s: number) => void,
): void {
  const m = s * 0.1;
  const r = s * 0.26;
  ctx.beginPath();
  ctx.roundRect(m, m, s - 2 * m, s - 2 * m, r);
  ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
  ctx.fill();
  ctx.lineWidth = s * 0.045;
  ctx.strokeStyle = 'rgba(6,10,18,0.5)';
  ctx.stroke();
  ctx.save();
  ctx.strokeStyle = '#fff';
  ctx.fillStyle = '#fff';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  draw(ctx, s);
  ctx.restore();
}

// elevator: a car with up + down arrows
function drawElevator(ctx: CanvasRenderingContext2D, s: number): void {
  const cx = s / 2;
  // up arrow (left of center), down arrow (right of center)
  const lw = s * 0.07;
  ctx.lineWidth = lw;
  // up
  const ux = cx - s * 0.14;
  ctx.beginPath();
  ctx.moveTo(ux, s * 0.66);
  ctx.lineTo(ux, s * 0.34);
  ctx.moveTo(ux - s * 0.08, s * 0.43);
  ctx.lineTo(ux, s * 0.33);
  ctx.lineTo(ux + s * 0.08, s * 0.43);
  ctx.stroke();
  // down
  const dx = cx + s * 0.14;
  ctx.beginPath();
  ctx.moveTo(dx, s * 0.34);
  ctx.lineTo(dx, s * 0.66);
  ctx.moveTo(dx - s * 0.08, s * 0.57);
  ctx.lineTo(dx, s * 0.67);
  ctx.lineTo(dx + s * 0.08, s * 0.57);
  ctx.stroke();
}

// escalator: a diagonal incline with steps + an up chevron
function drawEscalator(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.lineWidth = s * 0.07;
  // incline rail bottom-left -> top-right
  ctx.beginPath();
  ctx.moveTo(s * 0.26, s * 0.68);
  ctx.lineTo(s * 0.74, s * 0.34);
  ctx.stroke();
  // a couple of step ticks
  ctx.lineWidth = s * 0.055;
  for (const t of [0.36, 0.56, 0.76]) {
    const x = s * (0.26 + (0.74 - 0.26) * t);
    const y = s * (0.68 + (0.34 - 0.68) * t);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + s * 0.1);
    ctx.stroke();
  }
  // up chevron at the top
  ctx.lineWidth = s * 0.06;
  ctx.beginPath();
  ctx.moveTo(s * 0.66, s * 0.34);
  ctx.lineTo(s * 0.78, s * 0.28);
  ctx.lineTo(s * 0.8, s * 0.4);
  ctx.stroke();
}

// ── POWER OUTAGE: rounded badge, severity color, white lightning bolt ─────────

// A centered lightning bolt, filled white with a faint dark edge for contrast.
function drawBolt(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.beginPath();
  ctx.moveTo(s * 0.56, s * 0.24);
  ctx.lineTo(s * 0.37, s * 0.55);
  ctx.lineTo(s * 0.49, s * 0.55);
  ctx.lineTo(s * 0.43, s * 0.78);
  ctx.lineTo(s * 0.64, s * 0.45);
  ctx.lineTo(s * 0.52, s * 0.45);
  ctx.lineTo(s * 0.585, s * 0.24);
  ctx.closePath();
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.lineWidth = s * 0.03;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(6,10,18,0.45)';
  ctx.stroke();
}

// ── HARBOR BUOY: tapered float, white band, beacon light, waterline waves ─────

function drawBuoy(ctx: CanvasRenderingContext2D, s: number, color: [number, number, number]): void {
  const cx = s / 2;
  const bodyTop = s * 0.4;
  const bodyBot = s * 0.78;
  const halfTop = s * 0.13;
  const halfBot = s * 0.2;
  const colStr = `rgb(${color[0]},${color[1]},${color[2]})`;
  const edge = 'rgba(6,10,18,0.55)';
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // mast from the top of the float up to the beacon
  ctx.beginPath();
  ctx.moveTo(cx, bodyTop);
  ctx.lineTo(cx, s * 0.24);
  ctx.lineWidth = s * 0.05;
  ctx.strokeStyle = edge;
  ctx.stroke();

  // beacon light on top (warm) so the buoy reads as a navigation marker
  ctx.beginPath();
  ctx.arc(cx, s * 0.18, s * 0.07, 0, Math.PI * 2);
  ctx.fillStyle = '#ffd23f';
  ctx.fill();
  ctx.lineWidth = s * 0.03;
  ctx.strokeStyle = edge;
  ctx.stroke();

  // tapered float hull with a rounded bottom
  const round = s * 0.04;
  ctx.beginPath();
  ctx.moveTo(cx - halfTop, bodyTop);
  ctx.lineTo(cx + halfTop, bodyTop);
  ctx.lineTo(cx + halfBot, bodyBot - round);
  ctx.quadraticCurveTo(cx + halfBot, bodyBot, cx + halfBot - round, bodyBot);
  ctx.lineTo(cx - halfBot + round, bodyBot);
  ctx.quadraticCurveTo(cx - halfBot, bodyBot, cx - halfBot, bodyBot - round);
  ctx.closePath();
  ctx.fillStyle = colStr;
  ctx.fill();
  ctx.lineWidth = s * 0.045;
  ctx.strokeStyle = edge;
  ctx.stroke();

  // white band across the float (narrower than the hull so it never overflows)
  const bandY = (bodyTop + bodyBot) / 2;
  const bandHalf = halfTop * 0.95;
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.fillRect(cx - bandHalf, bandY - s * 0.05, bandHalf * 2, s * 0.1);

  // waterline waves at the float base
  const wy = s * 0.86;
  const ww = s * 0.3;
  ctx.beginPath();
  ctx.moveTo(cx - ww, wy);
  ctx.quadraticCurveTo(cx - ww * 0.5, wy - s * 0.05, cx, wy);
  ctx.quadraticCurveTo(cx + ww * 0.5, wy + s * 0.05, cx + ww, wy);
  ctx.lineWidth = s * 0.05;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.stroke();
}

// ── Public API ────────────────────────────────────────────────────────────────

export type FloodBandName = 'minor' | 'moderate' | 'major';

/**
 * Returns an IconDef for a flood-sensor droplet glyph.
 * Anchor is at the droplet tip (bottom center), so the pin points at the sensor.
 * The tip falls at s*0.96 in logical px; anchorY = sizePx * 0.96.
 */
export function floodGlyph(
  band: FloodBandName,
  color: [number, number, number],
  sizePx: number,
): IconDef {
  return bake(
    band,
    sizePx,
    (ctx, s) => drawDroplet(ctx, s, color),
    { x: sizePx / 2, y: sizePx * 0.96 },
  );
}

/**
 * Returns an IconDef for an elevator/escalator sign badge.
 * Center-anchored so the badge sits over the equipment location.
 */
export function eneGlyph(
  kind: 'elevator' | 'escalator',
  ada: boolean,
  color: [number, number, number],
  sizePx: number,
): IconDef {
  const drawInner = kind === 'elevator' ? drawElevator : drawEscalator;
  return bake(
    `${kind}-${ada}`,
    sizePx,
    (ctx, s) => eneBadge(ctx, s, color, drawInner),
    { x: sizePx / 2, y: sizePx / 2 },
  );
}

/**
 * Returns an IconDef for a power-outage badge: a rounded sign with a white
 * lightning bolt, colored by severity. Center-anchored over the cluster.
 * `key` distinguishes severity tiers so each (tier, size) rasterizes once.
 */
export function outageGlyph(
  key: string,
  color: [number, number, number],
  sizePx: number,
): IconDef {
  return bake(
    `outage-${key}-${sizePx}`,
    sizePx,
    (ctx, s) => eneBadge(ctx, s, color, drawBolt),
    { x: sizePx / 2, y: sizePx / 2 },
  );
}

/**
 * Returns an IconDef for a harbor navigation-buoy glyph.
 * Anchored at the waterline (bottom center) so the float sits on the station.
 */
export function buoyGlyph(
  color: [number, number, number],
  sizePx: number,
): IconDef {
  return bake(
    `buoy-${sizePx}`,
    sizePx,
    (ctx, s) => drawBuoy(ctx, s, color),
    { x: sizePx / 2, y: sizePx * 0.84 },
  );
}
